import { FC, createElement, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Layout, Menu, Spin, Dropdown, Badge } from 'antd';
import { MenuUnfoldOutlined, MenuFoldOutlined, DownOutlined, UserOutlined, PoweroffOutlined } from '@ant-design/icons';
import cs from 'classnames';
import styles from './index.module.less';

import IconFont from '@/common/components/IconFont';
import ModifyPassword from './components/ModifyPassword';
import MicroApplicationRoute from '@/common/components/Qiankun/MicroApplicationRoute';

import { accumulation, deepCopy, matchQuery, recursionEach, isMobile } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { onLoading } from '@/common/document-event/on';
import { removeLoading } from '@/common/document-event/remove';
import { dispatchLogout } from '@/common/document-event/dispatch';
import { BasicType, HeaderStatus } from './ts-config';
import { asideMenuConfig } from './menuConfig';
import { componentDocsMenu } from './componentMenuConfig';
import { get } from '@/common/request';
import MicroApplicationRouter from '@/common/components/Qiankun/MicroApplicationRouter';
import Logo from 'src/layout/components/Logo';

// const { SubMenu } = Menu;
const { Header, Sider } = Layout;
const BasicLayout: FC<BasicType> = ({ location, routeList, isOpen, children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectKeys, setSelectKeys] = useState<string[]>([location.to.pathname]);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [defaultOpenKeys, setDefaultOpenKeys] = useState<Array<string>>([]);
  const [modifyPsd, setModifyPsd] = useState<boolean>(false);
  const [totalAgency, setTotalAgency] = useState<number>(0); // 代办总数tag显示值
  const userInfo = useSelector((state: any) => state.common.userInfo);


  /* methods */
  const methods = useMethods({
    handleOpenChange(keys) {
      setDefaultOpenKeys(keys);
    },
    toggle() {
      setCollapsed(!collapsed);
    },
    handleMenuClick({ key }) {
      switch (key) {
        case HeaderStatus.LOGIN_OUT:
          dispatchLogout();
          break;
        case HeaderStatus.RESET_PASSWORD:
          setModifyPsd(true);
          break;
        default:
          break;
      }
    },
    getTotalAgency() { // 获取代办总数
      // https://yapi.lanhanba.com/project/307/interface/api/33963
      get('/requirement/todoNumber', {}, { needCancel: false, needHint: false }).then((data) => {
        setTotalAgency(data);
      });
    },
    toKunlun() {
      window.location.href = `${process.env.KUNLUN_URL}`;
    }
  });
  /* effects */
  useEffect(() => {
    setSelectKeys(location.to.pathname);
    if (asideMenuConfig) {
      recursionEach(asideMenuConfig, 'children', (item: any) => {
        if (item.uri === location.to.pathname && item.parent) {
          // 有父级节点才默认展开
          setDefaultOpenKeys([item.parent.uri]);
          return true;
        } else {
          return false;
        }
      });
    }
  }, [location]);
  useEffect(() => {
    // !isOpen && methods.getTotalAgency();
    onLoading(setLoading);
    return () => {
      removeLoading(setLoading);
    };
  }, []);
  useEffect(() => {
    if (isMobile()) { // 如果是移动端默认收起侧边栏
      methods.toggle();
    }
  }, []);
  /* computed */
  // 获取到各页面page.config.ts里的meta值
  const meta: any = useMemo(() => {
    const { pathname, search } = location.to;
    const metaName = matchQuery(search, 'metaName');
    const targetRoute = routeList?.find(item => {
      return item.path === pathname;
    });
    const result = deepCopy(targetRoute?.meta || {});
    if (metaName) {
      result.name = metaName;
    }
    return result;
  }, [location, routeList]);

  // 当前导航是页面导航还是组件文档导航
  const curAsideMenu = useMemo(() => {
    const tempComponentDocsMenu = Array.isArray(componentDocsMenu) ? componentDocsMenu : [];
    const isComponentDoc = meta?.isComponentDoc;
    isComponentDoc && setDefaultOpenKeys(tempComponentDocsMenu.map(item => item.name)); // 如果是组件文档页面，默认展开所有导航
    return isComponentDoc ? tempComponentDocsMenu : asideMenuConfig;
  }, [meta]);

  const renderMenu = (menus) => {
    const icon = typeof menus.icon === 'string' ? <IconFont iconHref={menus.icon}/> : (menus.icon && <menus.icon />);
    if (menus.children) {
      return {
        label: menus.name,
        key: menus.name,
        icon: icon,
        children: menus.children.map((childItem) => renderMenu(childItem))
      };
    }
    const demandsMenu = <Link to={menus.path}>{menus.name} <Badge className={styles.badge} dot={!!accumulation(totalAgency)} offset={[2, 4]}/></Link>;
    return {
      label: demandsMenu,
      key: menus.path,
      icon: icon
    };
  };
  return (
    <div className={cs(isOpen && styles.isOpen)}>
      <Spin spinning={loading}>
        <Layout>
          {
            !isOpen && <Sider trigger={null} collapsible className={styles.siderWrap} collapsed={collapsed} width={200}>
              <Logo collapsed={collapsed}/>
              <Menu
                theme='dark'
                mode='inline'
                selectedKeys={selectKeys}
                openKeys={defaultOpenKeys}
                onOpenChange={methods.handleOpenChange}
                items={curAsideMenu.map((item) => renderMenu(item))}
              />
            </Sider>
          }
          <Layout className={styles.main} style={{ marginLeft: collapsed ? 80 : 200 }}>
            {
              !isOpen && <Header className={styles.siteLayout}>
                <div className={styles.headerLeft}>
                  {createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                    className: styles.trigger,
                    onClick: methods.toggle,
                  })}
                  <span className={styles.crumbs}>
                    {
                      meta.crumbs ? meta.crumbs.name.map((item, index) => {
                        return (
                          <span key={index}>
                            <Link to={meta.crumbs.path[index]}>{item}</Link>/
                          </span>
                        );
                      }) : undefined
                    }
                    {meta.name}
                    {/* 选址需求/详情 */}
                  </span>
                </div>
                <div className={styles.userInfo}>
                  {
                    process.env.NODE_ENV === 'production' && <>
                      <Dropdown
                        className={styles.dropdownCursor}
                        placement='bottom'
                        arrow
                        overlayStyle={{ width: 'max-content' }}
                        menu={{
                          items: [{ label: '昆仑', key: 'kunlun' }],
                          onClick: methods.toKunlun
                        }}
                      >
                        <i className={styles.menu}/>
                      </Dropdown>
                      <div className={styles.divider}></div>
                    </>
                  }
                  <Dropdown
                    className={styles.nameOuter}
                    overlayStyle={{ width: 'max-content' }}
                    menu={{
                      items: [
                        { label: '修改密码', key: HeaderStatus.RESET_PASSWORD, icon: <UserOutlined /> },
                        { label: '退出登陆', key: HeaderStatus.LOGIN_OUT, icon: <PoweroffOutlined /> }
                      ],
                      onClick: methods.handleMenuClick
                    }}
                  >
                    <span>
                      {userInfo?.name} <DownOutlined className={styles.logo} />
                    </span>
                  </Dropdown>
                </div>
              </Header>
            }
            <div className={styles.layoutMain}>
              <MicroApplicationRouter>
                {process.env.NODE_ENV === 'production'
                  ? <MicroApplicationRoute
                    pathname='/lhb-micro-resource'
                    entry='/res-micro-app/'
                  />
                  : <MicroApplicationRoute
                    pathname='/lhb-micro-resource'
                    entry='http://localhost:8006'
                  />
                }
                {children}
              </MicroApplicationRouter>
            </div>
          </Layout>
        </Layout>
        {
          !isOpen && <ModifyPassword visible={modifyPsd} onClose={() => setModifyPsd(false)} />
        }
      </Spin>
    </div>
  );
};

export default BasicLayout;
